import React, {
  RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {styled} from "@reside/ui";

const TRACK_WIDTH_PX = 128;
/** Inner track height; grid uses the same — with panel padding (~12px) total ≈ 60px. */
const TRACK_HEIGHT_PX = 48;
const TICK_GAP_PX = 3;
const VIEWPORT_MIN_WIDTH_PX = 14;

type ScrollMetrics = Readonly<{
  scrollLeft: number;
  scrollWidth: number;
  clientWidth: number;
}>;

type Props = Readonly<{
  scrollRef: RefObject<HTMLElement | null>;
  columnCount: number;
  /**
   * `vertical` — tall strips in a row (Kanban columns).
   * `horizontal` — stacked horizontal bars (wide table columns).
   */
  tickLayout?: "vertical" | "horizontal";
}>;

/**
 * Horizontal scroll minimap: column ticks + viewport outline, click/drag on the
 * track to scroll (native horizontal scrollbar is hidden on WebKit).
 */
export const DashboardBoardScrollMinimap = ({
  scrollRef,
  columnCount,
  tickLayout = "vertical",
}: Props) => {
  const [metrics, setMetrics] = useState<ScrollMetrics | null>(null);
  const rafRef = useRef<number>(0);
  const trackRef = useRef<HTMLDivElement | null>(null);

  const readMetrics = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    setMetrics({
      scrollLeft: el.scrollLeft,
      scrollWidth: el.scrollWidth,
      clientWidth: el.clientWidth,
    });
  }, [scrollRef]);

  const scheduleRead = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      readMetrics();
    });
  }, [readMetrics]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return undefined;
    }

    readMetrics();
    el.addEventListener("scroll", scheduleRead, {passive: true});
    const ro = new ResizeObserver(scheduleRead);
    ro.observe(el);
    const firstChild = el.firstElementChild;
    if (firstChild instanceof HTMLElement) {
      ro.observe(firstChild);
    }

    return () => {
      el.removeEventListener("scroll", scheduleRead);
      ro.disconnect();
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [readMetrics, scheduleRead, scrollRef]);

  const applyPointerClientX = useCallback(
    (clientX: number) => {
      const track = trackRef.current;
      const el = scrollRef.current;
      if (!track || !el) {
        return;
      }
      const rect = track.getBoundingClientRect();
      const x = clientX - rect.left;
      const ratio = Math.min(1, Math.max(0, x / rect.width));
      const maxS = Math.max(0, el.scrollWidth - el.clientWidth);
      el.scrollLeft = ratio * maxS;
    },
    [scrollRef],
  );

  const onTrackPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === "mouse" && e.button !== 0) {
        return;
      }
      const track = trackRef.current;
      if (!track) {
        return;
      }
      applyPointerClientX(e.clientX);
      track.setPointerCapture(e.pointerId);
      const onMove = (ev: PointerEvent) => {
        applyPointerClientX(ev.clientX);
      };
      const onEnd = (ev: PointerEvent) => {
        if (track.hasPointerCapture(ev.pointerId)) {
          track.releasePointerCapture(ev.pointerId);
        }
        track.removeEventListener("pointermove", onMove);
        track.removeEventListener("pointerup", onEnd);
        track.removeEventListener("pointercancel", onEnd);
      };
      track.addEventListener("pointermove", onMove);
      track.addEventListener("pointerup", onEnd);
      track.addEventListener("pointercancel", onEnd);
    },
    [applyPointerClientX],
  );

  if (
    !metrics ||
    metrics.scrollWidth <= metrics.clientWidth + 1 ||
    columnCount < 1
  ) {
    return null;
  }

  const maxScroll = Math.max(1, metrics.scrollWidth - metrics.clientWidth);
  const viewportRatio = metrics.clientWidth / metrics.scrollWidth;
  const thumbWidth = Math.max(
    VIEWPORT_MIN_WIDTH_PX,
    Math.min(TRACK_WIDTH_PX, TRACK_WIDTH_PX * viewportRatio),
  );
  const maxThumbLeft = TRACK_WIDTH_PX - thumbWidth;
  const thumbLeft =
    maxScroll > 0
      ? Math.min(
          maxThumbLeft,
          Math.max(0, (metrics.scrollLeft / maxScroll) * maxThumbLeft),
        )
      : 0;

  const horizontalTicks = tickLayout === "horizontal";
  const trackHeightPx = TRACK_HEIGHT_PX;

  return (
    <MinimapRoot aria-hidden>
      <MinimapPanel>
        <MinimapTrack
          ref={trackRef}
          style={{
            width: TRACK_WIDTH_PX,
            height: trackHeightPx,
          }}
          onPointerDown={onTrackPointerDown}
        >
          <MinimapTicks $gap={TICK_GAP_PX} $horizontal={horizontalTicks}>
            {Array.from({length: columnCount}, (_, i) => (
              <MinimapTick key={i} $horizontal={horizontalTicks} />
            ))}
          </MinimapTicks>
          <MinimapViewport
            style={{
              width: thumbWidth,
              left: thumbLeft,
            }}
          />
        </MinimapTrack>
      </MinimapPanel>
    </MinimapRoot>
  );
};

const MinimapRoot = styled.div`
  position: absolute;
  right: 130px;
  bottom: 15px;
  z-index: 5;
`;

const MinimapPanel = styled.div`
  padding: 6px 8px;
  border-radius: 8px;
  background-color: ${({theme}) => theme.color.white};
  box-shadow: 0 3px 8px hsla(214, 27.5%, 62.2%, 0.35);
`;

const MinimapTrack = styled.div`
  position: relative;
  cursor: pointer;
  touch-action: none;
`;

const MinimapTicks = styled.div<{$gap: number; $horizontal: boolean}>`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: ${({$horizontal}) => ($horizontal ? "column" : "row")};
  align-items: stretch;
  ${({$gap, $horizontal}) =>
    $horizontal ? `row-gap: ${$gap}px;` : `column-gap: ${$gap}px;`}
`;

const MinimapTick = styled.div<{$horizontal: boolean}>`
  flex: 1;
  min-width: 0;
  ${({$horizontal}) =>
    $horizontal
      ? `
    min-height: 0;
    width: 100%;
  `
      : ""}
  border-radius: 2px;
  background-color: ${({theme}) => theme.color.gray10};
`;

const MinimapViewport = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  box-sizing: border-box;
  border: 2px solid ${({theme}) => theme.color.primary};
  border-radius: 4px;
  background: transparent;
  pointer-events: none;
`;
